import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { DatePicker, Dropdown, NumericInput } from '@cambridgeassessment/cambridge-ui';
import { Component, ExamDate, ComponentSeries, SubmissionOffset } from '@cambridgeassessment/lib-ee-commons';
import { StyledBufferTitle, StyledRow, StyledTextFieldContainer } from '../styles';
import { brandedFC } from '../../../../../branding/brandedFC';
import { StyledInputContainer } from '../../../Series/Series.styles';
import { StyledTitle, StyledSeriesTitle, StyledExamTitle } from './styles';
import { currentTime } from '../../../../../util/time';
import { AdministrationZone } from '../../../../../types/AdministrationZone';
import _ from 'lodash';
import pluralize from 'pluralize';
import { displayNotification } from '../../../../../store/notification/actions';
import { SeriesData } from '../../../../../types/Series';

export enum DefaultSubmissionDate {
  OFFSET_DATE = 5,
  BUFFER_DATE = 14,
  POE_BUFFER_DATE = 0,
}

interface Props {
  addOrUpdateSeriesExamDate: (seriesId: ComponentSeries, examDate: ExamDate) => void;
  component: Component;
  disabled?: boolean;
  readOnly?: boolean;
  seriesData: SeriesData;
}

const DISPLAY_FORMAT = 'DD/MM/YYYY';
const dayValueFormatter = (days: number): string => pluralize('day', days, true);

const adminZones: AdministrationZone[] = [
  { zone: 'Zone 1', zoneId: '1' },
  { zone: 'Zone 2', zoneId: '2' },
  { zone: 'Zone 3', zoneId: '3' },
  { zone: 'Zone 4', zoneId: '4' },
  { zone: 'Zone 5', zoneId: '5' },
  { zone: 'Zone 6', zoneId: '6' },
];

const ExamDatePerZoneSelector: React.FC<Props> = brandedFC<Props>(
  ({ allowExaminedComponents }, { component, addOrUpdateSeriesExamDate, readOnly, disabled, seriesData }) => {
    const dispatch = useDispatch();
    const today = currentTime().toISOString();
    const upcomingSeries = _.compact(seriesData?.filter((series) => series.endDate > today));
    const upcomingComponentSeries = _.intersectionBy(component.series, upcomingSeries, 'id');

    const findExamDate = (examDates: ExamDate[] | undefined, zone: AdministrationZone): ExamDate | undefined => {
      const examDateByZoneId = examDates?.find((date: ExamDate) => date.zoneId === zone.zoneId);
      const examDateWithZoneIdNull = examDates?.find((date: ExamDate) => date.zoneId === null);

      return examDateByZoneId || examDateWithZoneIdNull;
    };

    const isPoE = component.isPoE;

    const [selectedSeries, setSelectedSeries] = useState<ComponentSeries | null>(
      upcomingComponentSeries.length < 1 ? null : upcomingComponentSeries[0],
    );
    const poeExamDate = component.series.find((x) => x.id === selectedSeries?.id)?.examDates[0];
    const poeBuffer = poeExamDate?.submissionOffset?.buffer || DefaultSubmissionDate.POE_BUFFER_DATE;

    const offsetDate = selectedSeries?.examDates[0]?.submissionOffset?.date ?? DefaultSubmissionDate.OFFSET_DATE;
    const bufferDate = selectedSeries?.examDates[0]?.submissionOffset?.buffer ?? DefaultSubmissionDate.BUFFER_DATE;
    const [selectedZone, setSelectedZone] = useState<AdministrationZone | null>(null);
    const [timeOfDay, setExamTimeOfDay] = useState<'AM' | 'PM' | null>(null);
    const [noModification, setNoModification] = useState<boolean>();
    const [submissionDate, setSubmissionDate] = useState<SubmissionOffset['date']>(isPoE ? 0 : offsetDate);
    const [submissionBuffer, setSubmissionBuffer] = useState<SubmissionOffset['buffer']>(
      isPoE ? poeBuffer : bufferDate,
    );
    const seriesToUpdate = upcomingComponentSeries.find(
      (series: ComponentSeries) => selectedSeries && series.id === selectedSeries.id,
    );
    const selectedSeriesExamDates = seriesToUpdate?.examDates;
    const seriesEnd = upcomingSeries.find((series) => selectedSeries && series.id === selectedSeries.id);
    const seriesEndingDate =
      !!seriesEnd && seriesEnd.submissionWindows.find((submissionWindow) => submissionWindow.buffer);
    const maxDate = seriesEndingDate && seriesEndingDate.buffer && new Date(seriesEndingDate.buffer);
    maxDate && maxDate.setDate(maxDate.getDate() - submissionDate - submissionBuffer);

    const hasNullExamDateZones = !!selectedSeriesExamDates?.every(
      ({ zone, zoneId }) => zone === null && zoneId === null,
    );

    const defaultExamDate = findExamDate(selectedSeriesExamDates, adminZones[0])?.date ?? '';
    const [examDate, setExamDate] = useState<string>(isPoE ? poeExamDate?.date ?? '' : defaultExamDate);

    const examObj: ExamDate = {
      date: examDate,
      examTimeOfDay: timeOfDay,
      zone: selectedZone && selectedZone.zone,
      zoneId: selectedZone && selectedZone.zoneId,
      submissionOffset: {
        date: submissionDate,
        buffer: submissionBuffer,
      },
      noModification,
    };

    const renderSeries = (item: ComponentSeries): string => item.name;

    const renderZones = (item: AdministrationZone): string => item.zone;

    const onSeriesSelected = (series: ComponentSeries): void => {
      if (isPoE) {
        setExamDate(series.examDates[0].date);
        setSubmissionBuffer(series.examDates[0].submissionOffset?.buffer ?? 10);
      }
      setSelectedSeries(series);

      const hasValidZoneInExamDates = !!series.examDates?.some(({ zone, zoneId }) => zone !== null && zoneId !== null);
      const selectedSeriesOffsetDate = series.examDates[0].submissionOffset?.date ?? DefaultSubmissionDate.OFFSET_DATE;
      const selectedSeriesBufferDate =
        series.examDates[0].submissionOffset?.buffer ?? DefaultSubmissionDate.BUFFER_DATE;

      if (hasValidZoneInExamDates) {
        onZoneSelected(adminZones[0]);
      }

      setSelectedZone(hasValidZoneInExamDates ? adminZones[0] : null);
      setExamDate(findExamDate(series.examDates, adminZones[0])?.date ?? '');
      setSubmissionDate(selectedSeriesOffsetDate);
      setSubmissionBuffer(selectedSeriesBufferDate);
    };

    const isExamBufferValid = (date: string, submissionOffsetDate: number, submissionOffsetBuffer: number): boolean => {
      const examSubmission = new Date(date);
      examSubmission.setDate(examSubmission.getDate() + submissionOffsetDate + submissionOffsetBuffer);

      return !!seriesEndingDate && !!seriesEndingDate.buffer && new Date(seriesEndingDate.buffer) < examSubmission;
    };

    const showSnackbar = (): void => {
      dispatch(displayNotification('You cannot set an exam buffer date that is later than the end of the series.'));
    };

    const disabledDatepicker = disabled || readOnly || !selectedZone;
    const disabledDropdown = disabled || readOnly;

    const onZoneSelected = (zone: AdministrationZone): void => {
      setSelectedZone(zone);
      const date = findExamDate(selectedSeriesExamDates, zone);
      const examDateForZone = date?.date || today;
      const examOffsetDate = date?.submissionOffset?.date ?? DefaultSubmissionDate.OFFSET_DATE;
      const examBufferDate = date?.submissionOffset?.buffer ?? DefaultSubmissionDate.BUFFER_DATE;

      const isValid = isExamBufferValid(examDateForZone, examOffsetDate, examBufferDate);
      setExamTimeOfDay(date?.examTimeOfDay || null);
      setExamDate(examDateForZone);
      setNoModification(date?.noModification);
      if (isValid) {
        showSnackbar();
        setSubmissionDate(0);
        setSubmissionBuffer(0);
        addOrUpdateSeriesExamDate(seriesToUpdate as ComponentSeries, {
          date: examDateForZone,
          examTimeOfDay: date?.examTimeOfDay || null,
          zone: zone.zone,
          zoneId: zone.zoneId,
          submissionOffset: { date: 0, buffer: 0 },
          noModification: date?.noModification,
        });
        return;
      }
      setSubmissionDate(examOffsetDate);
      setSubmissionBuffer(examBufferDate);
    };

    const changeSeriesExamDate = (examDateValue: string): void => {
      setExamDate(examDateValue);
      addOrUpdateSeriesExamDate(seriesToUpdate as ComponentSeries, { ...examObj, date: examDateValue });
    };

    const handleSubmissionOffsetChange = (offsetDate: number, offsetBuffer: number): void => {
      const isValid = isExamBufferValid(examDate, offsetDate, offsetBuffer);
      if (isValid) {
        showSnackbar();
        return;
      }
      setSubmissionDate(offsetDate);
      setSubmissionBuffer(offsetBuffer);
      addOrUpdateSeriesExamDate(seriesToUpdate as ComponentSeries, {
        ...examObj,
        submissionOffset: { date: offsetDate, buffer: offsetBuffer },
      });
    };

    if (!allowExaminedComponents) {
      return null;
    }

    return (
      <>
        <StyledTextFieldContainer>
          <StyledSeriesTitle data-qa-id='series-label'>Series</StyledSeriesTitle>
          <StyledInputContainer>
            <Dropdown
              disabled={disabledDropdown}
              data={upcomingComponentSeries}
              renderOption={renderSeries}
              renderSelectedOption={renderSeries}
              dataQaId='series-select'
              onChange={onSeriesSelected}
              value={selectedSeries}
              placeholder='Series'
              title='Select series'
            />
          </StyledInputContainer>
          {!component.isPoE && (
            <StyledInputContainer>
              <StyledTitle data-qa-id='admin-zone-label'>Administration Zone</StyledTitle>
              <Dropdown
                disabled={disabledDropdown || !selectedSeries || hasNullExamDateZones}
                data={adminZones}
                renderOption={renderZones}
                renderSelectedOption={renderZones}
                dataQaId='adminZone-select'
                onChange={onZoneSelected}
                value={selectedZone}
                placeholder='Administration Zone'
                title='Select AdminZone'
              />
            </StyledInputContainer>
          )}
          <StyledInputContainer>
            <StyledExamTitle data-qa-id='exam-date-label'>Exam date</StyledExamTitle>
            <DatePicker
              key={examDate}
              disabled={disabledDatepicker || noModification || hasNullExamDateZones}
              value={examDate}
              format={DISPLAY_FORMAT}
              onChange={changeSeriesExamDate}
              dataQaId='exam-date'
              maxDate={maxDate}
            />
          </StyledInputContainer>
        </StyledTextFieldContainer>
        <StyledRow>
          <StyledTextFieldContainer>
            <StyledBufferTitle data-qa-id='submission-date-label'>Change component submission date</StyledBufferTitle>
            <NumericInput
              disabled={disabledDatepicker || hasNullExamDateZones}
              onChange={(date: number): void => handleSubmissionOffsetChange(date, submissionBuffer)}
              value={submissionDate}
              displayFormatter={dayValueFormatter}
              minValue={0}
              dataQaId='submission-date'
              helperText='Set the number of days after the exam date. This is the deadline advertised to customers.'
            />
          </StyledTextFieldContainer>
          <StyledTextFieldContainer>
            <StyledBufferTitle data-qa-id='submission-buffer-date-label'>
              Change component submission buffer date
            </StyledBufferTitle>
            <NumericInput
              disabled={disabled || readOnly || isPoE || hasNullExamDateZones}
              onChange={(buffer: number): void => handleSubmissionOffsetChange(submissionDate, buffer)}
              value={submissionBuffer}
              displayFormatter={dayValueFormatter}
              minValue={0}
              dataQaId='submission-buffer-date'
              helperText='Set the number of days after the submission deadline that the system will still accept submissions. Customers do NOT see this date.'
            />
          </StyledTextFieldContainer>
        </StyledRow>
      </>
    );
  },
);

export default ExamDatePerZoneSelector;
